/**
 * AI extras schemas — request/response envelopes around the OpenAI client.
 *
 * The actual structured output schema is `AIOutput` (see `ai-output.ts`).
 * The schemas below describe the *transport* layer of `AIService.chatCompletion`:
 *   • the request envelope (prompt + history + json_schema + opts)
 *   • the usage report (tokens + cost) returned by OpenAI
 *   • the typed result wrapper `AICompletionResult<T>`
 *
 * Story 2.9 — Epic E2 §2.D — `apps/api/src/services/ai/ai.service.ts` is the
 * sole consumer for now. Webhook + worker code can import these to type the
 * boundary deterministically.
 *
 * Constitution Article IV (No Invention): every field traces to PRD §FR5
 * ("AI estruturada via JSON output") and Epic E2 Story 2.9 acceptance
 * criteria (§Audit de tokens consumidos por turno + chatCompletion timeout
 * 8s + Levenshtein anti-repetição).
 */
import { Type } from '@sinclair/typebox';
/**
 * Role labels for the chat history. Mirrors OpenAI's chat completions
 * message shape.
 */
export const AIRole = Type.Union([
    Type.Literal('system'),
    Type.Literal('user'),
    Type.Literal('assistant'),
]);
/**
 * One historical turn passed back to the model. We keep it strict: no tool
 * calls, no images — those are explicitly out of scope (Epic E2 §Escopo Fora
 * mentions Whisper/audio as pós-MVP; image is similarly deferred).
 */
export const AIHistoryMessage = Type.Object({
    role: AIRole,
    content: Type.String({ minLength: 1 }),
});
/**
 * Optional per-call overrides surfaced by `AIService.chatCompletion(opts?)`.
 * All fields are optional — defaults come from `env.ts` (`OPENAI_DEFAULT_MODEL`,
 * `OPENAI_TIMEOUT_MS`).
 */
export const AICompletionOptions = Type.Object({
    /** Override the primary model — useful for A/B tests against `gpt-4o`. */
    model: Type.Optional(Type.String({ minLength: 1 })),
    /** Override the abort timeout (ms). Falls back to env default 8000ms. */
    timeoutMs: Type.Optional(Type.Integer({ minimum: 1 })),
    /** OpenAI temperature; default 0 for deterministic JSON output. */
    temperature: Type.Optional(Type.Number({ minimum: 0, maximum: 2 })),
    /** Cap on output tokens — null/undefined lets the model decide. */
    maxOutputTokens: Type.Optional(Type.Integer({ minimum: 1 })),
    /** Anti-repetition similarity threshold (0..1). Default 0.80 per Epic E2 §AC. */
    antiRepetitionThreshold: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
    /** Cap on anti-repetition retries. Default 2 retries (3 attempts max). */
    maxAntiRepetitionRetries: Type.Optional(Type.Integer({ minimum: 0, maximum: 5 })),
    /** Up to N most-recent assistant outputs to compare against (anti-repetition window). */
    recentAssistantOutputs: Type.Optional(Type.Array(Type.String(), { maxItems: 10 })),
    /** Correlation id forwarded to the audit log. */
    correlationId: Type.Optional(Type.String({ minLength: 1 })),
    /** Optional name for the JSON schema sent to OpenAI (must be a-zA-Z0-9_-, max 64). */
    schemaName: Type.Optional(Type.String({ pattern: '^[A-Za-z0-9_-]{1,64}$' })),
});
/**
 * Request envelope — what `chatCompletion` accepts (modulo the runtime
 * `schema` argument, which is a TypeBox value, not JSON-serializable).
 */
export const AICompletionRequest = Type.Object({
    prompt: Type.String({ minLength: 1 }),
    history: Type.Array(AIHistoryMessage, { default: [] }),
    options: Type.Optional(AICompletionOptions),
});
/**
 * Token usage as returned by OpenAI (`response.usage`). All counters default
 * to 0 if the SDK omits them on a given response.
 */
export const AICompletionUsage = Type.Object({
    promptTokens: Type.Integer({ minimum: 0 }),
    completionTokens: Type.Integer({ minimum: 0 }),
    totalTokens: Type.Integer({ minimum: 0 }),
    /** Cost in USD estimated from `ai-pricing.ts`. May be 0 when the model is
     *  unknown to the pricing table. */
    estimatedCostUsd: Type.Number({ minimum: 0 }),
});
/**
 * `AICompletionResult<T>` — generic wrapper around the validated output.
 *
 * Returns a TypeBox schema factory: callers pass the schema for the parsed
 * `output` and get back a fully-typed `Static<...>` envelope.
 *
 * Usage:
 * ```ts
 * import { AIOutput } from './ai-output.js'
 * const Result = AICompletionResult(AIOutput)
 * type Result = Static<ReturnType<typeof AICompletionResult<typeof AIOutput>>>
 * ```
 */
export function AICompletionResult(output) {
    return Type.Object({
        output,
        usage: AICompletionUsage,
        /** Model that actually returned the response — may be the fallback. */
        model: Type.String({ minLength: 1 }),
        /** Number of attempts spent (1 = first try succeeded). */
        attempts: Type.Integer({ minimum: 1 }),
    });
}
//# sourceMappingURL=ai-extras.js.map